import type { BoardSummary, TaskSummary } from '@obras/shared';
import { formatDate, formatDateTime, TASK_URGENCY_LABELS } from '@obras/shared';
import { CalendarClock } from 'lucide-react';

import { Button } from '../../components/ui/Button.js';
import { Checkbox } from '../../components/ui/Field.js';
import { Avatar } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { cn } from '../../lib/cn.js';
import { useToggleChecklistItem } from './api.js';
import { FLAG_BG, isSignalledUrgency, URGENCY_STYLE } from './styles.js';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * Leitura da tarefa.
 *
 * Separada do formulário porque quem participa vê tudo mas não reescreve o
 * pedido de quem montou o trabalho — só o autor e o gestor editam, e é o
 * servidor quem garante. O que continua ao alcance de todo participante aqui é
 * marcar o checklist: registrar o próprio avanço é o motivo de a tarefa ter
 * sido compartilhada.
 */
export function TaskViewModal({
  open,
  onClose,
  board,
  task,
}: {
  open: boolean;
  onClose: () => void;
  board: BoardSummary;
  task: TaskSummary;
}) {
  const toggle = useToggleChecklistItem(board.id);
  const urgency = isSignalledUrgency(task.urgency) ? URGENCY_STYLE[task.urgency] : null;
  const UrgencyIcon = urgency?.icon;

  const done = task.checklist.filter((item) => item.isDone).length;
  const column = board.columns.find(
    (item) => item.id === (board.isPersonal ? task.stage : task.columnId),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={task.title}
      description={board.isPersonal ? 'Quadro Pessoal' : board.name}
      /*
       * Só fechar. Editar é o lápis do cartão, e repetir a porta aqui dobrava
       * o caminho para a mesma coisa — esta tela é para ler e marcar checklist.
       */
      footer={<Button onClick={onClose}>Fechar</Button>}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          {task.color ? (
            <span
              className={cn('size-2.5 rounded-full', FLAG_BG[task.color])}
              aria-label="Etiqueta de cor"
            />
          ) : null}

          {column ? (
            <span className="rounded-xs bg-muted px-2 py-0.5 text-[12px]">{column.name}</span>
          ) : null}

          {urgency && UrgencyIcon ? (
            <span className={cn('flex items-center gap-1 text-[12px]', urgency.className)}>
              <UrgencyIcon size={13} strokeWidth={1.75} aria-hidden />
              {TASK_URGENCY_LABELS[task.urgency]}
            </span>
          ) : null}

          {task.dueAt ? (
            <span className="flex items-center gap-1 text-[12px] text-muted-foreground">
              <CalendarClock size={13} strokeWidth={1.5} aria-hidden />
              <span className="tabular">{formatDate(task.dueAt)}</span>
            </span>
          ) : null}
        </div>

        {task.description ? (
          <Section title="Descrição">
            <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{task.description}</p>
          </Section>
        ) : null}

        <Section title="Participantes">
          <ul className="flex flex-wrap gap-2">
            {task.participants.map((person) => (
              <li
                key={person.id}
                className="flex items-center gap-2 rounded-sm bg-muted py-1 pr-2.5 pl-2 text-[13px]"
              >
                <Avatar name={person.name} url={person.avatarUrl} size={18} />
                <span className="max-w-40 truncate">{person.name}</span>
                {person.id === task.createdBy.id ? (
                  <span className="text-[11px] text-muted-foreground">autor</span>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>

        {task.checklist.length > 0 ? (
          <Section title="Checklist">
            <p className="tabular text-[13px] text-muted-foreground">
              {done} de {task.checklist.length} concluídos
            </p>
            <ul className="mt-1 flex flex-col gap-2.5">
              {task.checklist.map((item) => (
                <li key={item.id}>
                  {/*
                    Marcado, o item mostra quem marcou e tranca para os demais:
                    é registro de autoria, e deixar um terceiro desmarcar
                    apagaria o trabalho de quem conferiu.
                  */}
                  <Checkbox
                    label={item.content}
                    checked={item.isDone}
                    disabled={!item.canToggle}
                    {...(item.doneBy
                      ? {
                          hint: `Concluído por ${item.doneBy.name}${
                            item.doneAt ? ` · ${formatDateTime(item.doneAt)}` : ''
                          }`,
                        }
                      : {})}
                    onChange={(event) =>
                      void toggle.mutateAsync({
                        taskId: task.id,
                        itemId: item.id,
                        isDone: event.target.checked,
                      })
                    }
                  />
                </li>
              ))}
            </ul>
          </Section>
        ) : null}
      </div>
    </Modal>
  );
}
